import billingApi from '@/lib/billingApi'
import type { ProjectZomboidVersionSelection } from '@/lib/projectZomboidVersions'
import type { PackagePublicTerms } from '@/lib/registrationIntent'

const base = '/hoard-api/account/backups'
const panelUuid = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i

export interface ReplacementQuote {
  intent_uuid: string
  quote_id: string
  quote_hash: string
  expires_at: string
  terms: { version_id: number; minimum_minor: number; ceiling_minor: number; currency: string }
  public_terms: PackagePublicTerms
  allocation: Record<string, unknown>
  source_backup_uuid: string
  source_server_uuid: string
  egg_id: number
  location_id: number
  pricing_basis: 'new_purchase'
  new_purchase_authorization_text: string
  recurring_authorization_text: string
  initial_minimum_minor: number
  currency: string
  checkout_available?: boolean
}

export interface ReplacementAdmission {
  intent_uuid: string
  target_server_uuid: string
  collection_operation_key: string
  amount_minor: number
  currency: string
  state: 'payment_pending' | 'requires_action' | 'paid_restore_pending'
  replayed: boolean
}

export interface ReplacementStatus {
  intent_uuid: string
  target_server_uuid: string | null
  state: 'preparing' | 'payment_pending' | 'requires_action' | 'payment_held' | 'paid_restore_pending' | 'restoring' | 'restore_failed' | 'ready'
  collection_operation_key?: string | null
  collection_state?: string | null
  amount_minor?: number | null
  currency?: string | null
  restore_operation_id?: string | null
  restore_phase?: string | null
  error_message?: string | null
}

export interface ReplacementRestoreRetry {
  intent_uuid: string
  target_server_uuid: string
  restore_operation_id: string | null
  state: 'restoring' | 'restore_failed' | 'ready'
  phase: string | null
  replayed: boolean
}

export interface ReplacementConfiguration {
  name: string
  environment: Record<string, string>
  project_zomboid_version?: ProjectZomboidVersionSelection
}

function assertQuote(data: ReplacementQuote | undefined, backupUuid: string): ReplacementQuote {
  if (!data || data.source_backup_uuid !== backupUuid || data.pricing_basis !== 'new_purchase'
    || !data.intent_uuid || !data.quote_id || !data.quote_hash || !data.expires_at
    || !data.source_server_uuid || !Number.isSafeInteger(data.egg_id) || !data.terms
    || !data.public_terms || !data.allocation || !Number.isSafeInteger(data.initial_minimum_minor)
    || data.initial_minimum_minor <= 0 || data.currency !== data.terms.currency
    || !data.new_purchase_authorization_text
    || !data.recurring_authorization_text) {
    throw new Error('The replacement quote is incomplete. Review the offer again before continuing.')
  }
  return data
}

function assertAdmission(data: ReplacementAdmission | undefined, intentUuid: string): ReplacementAdmission {
  if (!data || data.intent_uuid !== intentUuid
    || !['payment_pending', 'requires_action', 'paid_restore_pending'].includes(data.state)
    || !panelUuid.test(data.target_server_uuid) || !data.collection_operation_key
    || !Number.isSafeInteger(data.amount_minor) || data.amount_minor <= 0 || !data.currency) {
    throw new Error('Replacement status is unavailable. Check the existing request before retrying.')
  }
  return data
}

export const backupReplacementApi = {
  async quote(backupUuid: string, packageVersionId: number, locationId: number, idempotencyKey: string): Promise<ReplacementQuote> {
    const response = await billingApi.post<{ data: ReplacementQuote }>(`${base}/${encodeURIComponent(backupUuid)}/replacement/quote`, {
      package_version_id: packageVersionId,
      location_id: locationId,
      idempotency_key: idempotencyKey,
    })
    return assertQuote(response.data?.data, backupUuid)
  },
  async admit(backupUuid: string, input: {
    intent_uuid: string
    package_version_id: number
    location_id: number
    quote_hash: string
    policy_acceptance: Record<string, unknown>
    config: ReplacementConfiguration
  }, idempotencyKey: string): Promise<ReplacementAdmission> {
    const response = await billingApi.post<{ data: ReplacementAdmission }>(
      `${base}/${encodeURIComponent(backupUuid)}/replacement`, input,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    return assertAdmission(response.data?.data, input.intent_uuid)
  },
  async status(backupUuid: string, intentUuid: string): Promise<ReplacementStatus> {
    const response = await billingApi.get<{ data: ReplacementStatus }>(
      `${base}/${encodeURIComponent(backupUuid)}/replacement/status`,
      { params: { intent_uuid: intentUuid } },
    )
    const data = response.data?.data
    if (!data || data.intent_uuid !== intentUuid
      || !['preparing', 'payment_pending', 'requires_action', 'payment_held', 'paid_restore_pending', 'restoring', 'restore_failed', 'ready'].includes(data.state)
      || (data.target_server_uuid != null && !panelUuid.test(data.target_server_uuid))) {
      throw new Error('Replacement status is unavailable. Check the existing request before retrying.')
    }
    return data
  },
  async retryRestore(backupUuid: string, intentUuid: string, idempotencyKey: string): Promise<ReplacementRestoreRetry> {
    const response = await billingApi.post<{ data: ReplacementRestoreRetry }>(
      `${base}/${encodeURIComponent(backupUuid)}/replacement/retry-restore`,
      { intent_uuid: intentUuid },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    const data = response.data?.data
    if (!data || data.intent_uuid !== intentUuid || !panelUuid.test(data.target_server_uuid)
      || !['restoring', 'restore_failed', 'ready'].includes(data.state)) {
      throw new Error('Restore retry status is unavailable. Check the existing target before retrying.')
    }
    return data
  },
}
